import React, { createContext, useState, useEffect, useContext } from 'react'
import * as tracker from '@btime/tracker'

import { getLocalStorageItem, setLocalStorageItem } from '../helpers/localStorage'
import { getSubdomain } from '../helpers/url'
import { useLoggedUser } from '@btime/react-session'

export const NewFeatureContext = createContext()
const { Provider } = NewFeatureContext

const FEATURES_PREFIX = 'NEW_FEATURE_'
const ALLOWED_WORKSPACES_TO_USE_PROCESSES = ['v2', 'interno', 'itau', 'backoffice']

export const NewFeatureProvider = ({ children }) => {
  const [features, setFeatures] = useState({})

  const genFeatureKey = (key, userId) => {
    return `${FEATURES_PREFIX}${key}_${userId}`
  }

  const initializeFeature = (key, userId) => {
    const featureKey = genFeatureKey(key, userId)
    const storedValue = getLocalStorageItem(featureKey) === 'true'

    setFeatures(prev => {
      const newState = {
        ...prev,
        [key]: storedValue
      }
      return newState
    })

    return storedValue
  }

  const toggleFeature = (key, userId) => {
    const featureKey = genFeatureKey(key, userId)
    const isActive = !features[key]
    setLocalStorageItem(featureKey, isActive.toString())
    setFeatures(prev => ({
      ...prev,
      [key]: isActive
    }))
    tracker.trackFeature(key, isActive)
  }

  const getFeatureState = (key, userId) => {
    if (features[key] === undefined) {
      const featureKey = genFeatureKey(key, userId)
      const storedValue = getLocalStorageItem(featureKey) === 'true'
      setFeatures(prev => ({
        ...prev,
        [key]: storedValue
      }))
      return storedValue
    }
    return features[key]
  }

  useEffect(() => {
    const handleStorageChange = event => {
      const key = event.key
      if (features[key] !== undefined) {
        setFeatures(prev => ({
          ...prev,
          [key]: event.isActive === 'true'
        }))
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [features])

  return <Provider value={{ getFeatureState, toggleFeature, initializeFeature }}>{children}</Provider>
}

export const useNewFeature = key => {
  const context = useContext(NewFeatureContext)
  const [isInitialized, setIsInitialized] = useState(false)
  const workspace = getSubdomain()
  const user = useLoggedUser()

  const isAllowedToUse = ALLOWED_WORKSPACES_TO_USE_PROCESSES.includes(workspace)

  useEffect(() => {
    if (!isInitialized && user) {
      context.initializeFeature(key, user.id)
      setIsInitialized(true)
    }
  }, [key, isInitialized, context, user])

  const isActive = context.getFeatureState(key, user?.id)
  const toggleFeature = () => context.toggleFeature(key, user?.id)

  return { isActive, toggleFeature, isAllowedToUse }
}
